import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

export interface ErrorMessage {
  messageKey: string;
  params?: Object;
  traceId?: string;
}

// Translated error toasts for HTTP errors: used by the error interceptor and by components that set SkipErrorToast
@Injectable({
  providedIn: 'root',
})
export class ErrorMessageService {
  // Codes the Core API sends in ApiException.details
  private static readonly codeMessages: Record<string, string> = {
    BookingsLimitExceeded: 'TOASTS.BOOKINGS.LIMIT_EXCEEDED',
    BookingsNameInUse: 'TOASTS.BOOKINGS.NAME_IN_USE',
  };

  private static readonly statusMessages: Record<number, string> = {
    0: 'TOASTS.ERROR.NETWORK',
    400: 'TOASTS.ERROR.BAD_REQUEST',
    401: 'TOASTS.ERROR.UNAUTHORIZED',
    403: 'TOASTS.ERROR.FORBIDDEN',
    404: 'TOASTS.ERROR.NOT_FOUND',
    409: 'TOASTS.ERROR.CONFLICT',
    500: 'TOASTS.ERROR.SERVER_ERROR',
  };

  constructor(
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {}

  resolve(error: HttpErrorResponse): ErrorMessage {
    const body = typeof error.error === 'object' ? error.error : null;
    const traceId = this.formatTraceId(body?.traceId);
    const codeKey = ErrorMessageService.codeMessages[body?.details];

    if (codeKey) return { messageKey: codeKey, traceId };

    const statusKey = ErrorMessageService.statusMessages[error.status];
    if (statusKey) return { messageKey: statusKey, traceId };

    return {
      messageKey: 'TOASTS.ERROR.UNKNOWN',
      params: { status: error.status },
      traceId,
    };
  }

  async showToast(
    error: HttpErrorResponse,
    titleKey: string,
    titleParams?: Object,
  ): Promise<void> {
    const { messageKey, params, traceId } = this.resolve(error);
    const message = await firstValueFrom(this.translate.get(messageKey, params));
    const title = await firstValueFrom(this.translate.get(titleKey, titleParams));

    // Only the message is rendered as HTML (the title stays text), so every part of it is escaped
    let html = this.escapeHtml(message);
    if (traceId) {
      const label = await firstValueFrom(this.translate.get('TOASTS.ERROR.TRACE_ID'));
      html += `<br><small>${this.escapeHtml(label)}: ${this.escapeHtml(traceId)}</small>`;
    }

    this.toastr.error(html, title, {
      enableHtml: true,
      timeOut: traceId ? 10000 : undefined,
    });
  }

  // ApiException and ProblemDetails carry the W3C id "00-<trace-id>-<span-id>-<flags>"; the trace-id is the
  // operation_Id in Application Insights
  private formatTraceId(traceId: unknown): string | undefined {
    if (typeof traceId !== 'string' || !traceId.trim()) return undefined;

    const w3c = /^[0-9a-f]{2}-([0-9a-f]{32})-[0-9a-f]{16}-[0-9a-f]{2}$/.exec(traceId);
    return w3c ? w3c[1] : traceId.trim().substring(0, 100);
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
